import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Check, Clock, Focus, Home, MapPin, User as UserIcon, X as XIcon, XCircle, AlertCircle } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { confirmDialog } from "../lib/confirm";
import EventChecklistDialog from "../components/dialogs/EventChecklistDialog";
import { computeStages } from "../components/BookingProgressTimeline";
import BookingDetailModal from "../components/BookingDetailModal";
import RequestDetailModal from "../components/RequestDetailModal";
import Avatar from "../components/Avatar";
import CalendarFilters, { CALENDAR_KIND_FILTERS, DEFAULT_FILTERS } from "../components/calendar/CalendarFilters";
import CalendarSwimlaneAbsences from "../components/calendar/CalendarSwimlaneAbsences";
import { STATUS_COLORS, BRAND_COLORS, withAlpha } from "../lib/bookingStatus";
import { initialsOf } from "../lib/initials";
import {
  HoverDraftPreview,
  CancelledDetailPanel,
  DaySelectionPanel,
} from "../components/calendar/CalendarDetailPanels";
import { deDateFull, deDateLong, deWeekdayDateFullTime } from "../lib/dateFormat";

function parseIso(d) { return new Date(d + "T00:00:00"); }
function fmtIso(d) { return d.toISOString().slice(0, 10); }
function sameDay(a, b) { return a.toDateString() === b.toDateString(); }
function daysInMonthGrid(year, month) {
  // Monday-start grid, 6 rows x 7 cols
  const first = new Date(year, month, 1);
  const startOffset = (first.getDay() + 6) % 7; // Monday=0
  const start = new Date(year, month, 1 - startOffset);
  const grid = [];
  for (let i = 0; i < 42; i++) {
    grid.push(new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  }
  return grid;
}

// Feb 2026 · User-Directive „Verantwortlicher im Kalender-Popup aus derselben
// Datenquelle wie in der Buchungsdetailansicht": der Kalender-Backend liefert
// pro Absence-Event bereits `assigned_to` (Buchungs-eigene Zuweisung) UND
// `assigned_concierge_id` (Objekt-Fallback). Diese Helper-Funktion sorgt dafür,
// dass das an das BookingDetailModal übergebene Payload dieselben Felder
// enthält wie in der `/team/bookings`-Detailansicht — der `BookingAssigneePicker`
// zieht seine Daten immer aus `booking.assigned_to` + `booking.assigned_concierge_id`.
function buildBookingModalPayload(full) {
  return {
    id: full.id.replace(/^booking-/, ""),
    customer_id: full.customer_id,
    property_id: full.property_id,
    from_date: full.start_date,
    to_date: full.end_date,
    services: full.services || [],
    disabled_standard_services: full.disabled_standard_services || [],
    completed_services: full.completed_services || [],
    service_notes: full.service_notes || {},
    notes: full.notes || "",
    status: full.status,
    cancelled_at: full.cancelled_at,
    // Verantwortlicher — 1:1 aus dem Booking-Doc (identisch zu /team/bookings).
    assigned_to: full.assigned_to || null,
    assigned_concierge_id: full.assigned_concierge_id || null,
    _property_name: full.property_name || "",
  };
}

// Ab-/Anreise-Kategorie wurde vollständig entfernt (Feb 2026):
// Ankunft/Abreise werden ausschließlich über den grauen/blauen
// Abwesenheits-Balken am oberen Rand des Tages kommuniziert — keine
// separaten Kacheln, Badges oder Filter-Chips mehr.
//
// Feb 2026 · KRITISCHER FIX Haus-Zustand-Removal: der frühere Menüpunkt
// „Haus Zustand" (`/team/inspections`) und das zugehörige Kalender-`inspection`-
// Kind sind vollständig entfernt — die Live-Status-Pflege läuft ausschließlich
// über den schema-getriebenen Live-Status pro Objekt im Kunden-Detail.
//
// Feb 2026 refactor: Filter-Definitionen + Detail-Panels wurden nach
// `components/calendar/` extrahiert (siehe Imports oben). Diese Datei
// enthält jetzt nur noch das Grid-Rendering, State-Verwaltung und die
// Add-Event-Form.

export default function TeamCalendar() {
  const { t } = useI18n();
  const { activeConciergeId, isImpersonatingConcierge, uiPrefs, setUiPrefs } = useAuth();
  const navigate = useNavigate();
  const today = useMemo(() => new Date(), []);
  const [searchParams, setSearchParams] = useSearchParams();
  // Feb 2026 · Deep-Link-Support · `?date=YYYY-MM-DD` (aus Request-Detail-
  // Modal „Im Kalender anzeigen"). Beim Mount setzen wir den Cursor auf
  // den Monat des Zieldatums, damit der Kalender direkt auf dem passenden
  // Tag aufklappt. Der Param wird nach Verarbeitung aus der URL entfernt.
  const _initialDate = (() => {
    const raw = searchParams.get("date");
    if (raw && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [y, m] = raw.split("-").map(Number);
      return { iso: raw, month: new Date(y, m - 1, 1) };
    }
    return null;
  })();
  const [cursor, setCursor] = useState(_initialDate ? _initialDate.month : new Date(today.getFullYear(), today.getMonth(), 1));
  const [highlightDay, setHighlightDay] = useState(_initialDate ? _initialDate.iso : null);
  const [filterOpenOnly, setFilterOpenOnly] = useState(
    () => searchParams.get("open") === "1"
  );
  // Deep-Link nur einmal verarbeiten (Ref-Guard); Highlight-Timer lebt in
  // einer Ref, damit ein Re-Run des Effekts ihn nicht vorzeitig löscht.
  const deepLinkHandled = useRef(false);
  const highlightTimer = useRef(null);
  useEffect(() => () => clearTimeout(highlightTimer.current), []);
  useEffect(() => {
    if (deepLinkHandled.current || !_initialDate) return;
    deepLinkHandled.current = true;
    if (searchParams.get("date")) {
      const next = new URLSearchParams(searchParams);
      next.delete("date");
      setSearchParams(next, { replace: true });
    }
    // Ziel-Tag scrollen + Highlight für 4 s.
    const el = document.querySelector(`[data-testid="cal-day-${_initialDate.iso}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    highlightTimer.current = setTimeout(() => setHighlightDay(null), 4000);
  }, [_initialDate, searchParams, setSearchParams]);
  const [events, setEvents] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [extras, setExtras] = useState([]);
  // Booking Detail modal — opened when clicking a NON-cancelled absence bar.
  // Team members see the Aufgaben-Checkliste (only enabled Inklusiv + gewählte
  // Zusatzleistungen), can toggle completion, and add extra portfolio items
  // via the "Service hinzufügen"-Button.
  const [openBooking, setOpenBooking] = useState(null); // booking-like object
  // Feb 2026 · User-Directive · Klick auf ein Service-Task-Badge im
  // Kalender öffnet die vollständige Detailansicht der zugehörigen
  // Anfrage — der Kreisweg Kalender → Anfrage → Kalender schließt sich.
  const [openRequest, setOpenRequest] = useState(null);
  const [requestsCache, setRequestsCache] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", kind: "task", priority: "normal", start_date: "", end_date: "", customer_id: "", property_id: "", notes: "", color: BRAND_COLORS.navy });
  // Filter-Chips persistieren serverseitig pro Nutzer (users.ui_prefs).
  const [filters, setFilters] = useState(() => {
    const saved = uiPrefs?.calendar_filters;
    return saved && typeof saved === "object" ? { ...DEFAULT_FILTERS, ...saved } : DEFAULT_FILTERS;
  });
  const filtersMountedRef = useRef(false);
  useEffect(() => {
    if (!filtersMountedRef.current) { filtersMountedRef.current = true; return; }
    setUiPrefs({ calendar_filters: filters });
  }, [filters, setUiPrefs]);
  const [selectedCell, setSelectedCell] = useState(null); // {date, customer_id}
  const [checklistEventId, setChecklistEventId] = useState(null); // manual event id (no prefix)
  // Detail panel opened when the team clicks a STORNIERT bar — shows sender,
  // Zeitpunkt, original desired range so the team can immediately understand
  // what was cancelled.
  const [cancelledDetail, setCancelledDetail] = useState(null); // absence event (e.g. { id, cancelled_at, ... })
  // Hover-Vorschau für ERLEDIGT-Balken — zeigt Draft-Zusammenfassung + Anzahl
  // erledigter Aufgaben direkt beim Hover, damit das Team ohne Klick weiß,
  // was im automatisch generierten Wochenbericht-Entwurf steht.
  const [hoverDraft, setHoverDraft] = useState(null); // { customerName, propertyName, draft, top, left }
  // Unread-cancellations badge on the "Stornierungen" chip. Refreshed on
  // every 15 s poll; reset to 0 the moment the team member activates the chip.
  const [cancellationsUnread, setCancellationsUnread] = useState(0);

  // ---- Filter interaction:
  //   • Kategorie-Chips (Abwesenheiten, Aufgaben, Wochenberichte)
  //     arbeiten im FOCUS-Modus: Klick pinnt die Ansicht auf diese eine
  //     Kategorie, alle anderen werden ausgegraut. Ein zweiter Klick oder
  //     "Alle anzeigen" hebt den Fokus wieder auf.
  //   • Stornierungen ist KEIN Focus-Filter, sondern ein UNABHÄNGIGER Toggle
  //     (additiv). Standardmäßig ausgeblendet; ein Klick blendet stornierte
  //     Abwesenheiten ADDITIV zur aktuellen Ansicht ein, ohne den restlichen
  //     Kalender zu verstecken.
  // Kunden-Fokus: pinnt den Kalender auf eine einzelne Person (Lane).
  // Wird via Klick auf den Namen am Anfang eines Absence-Balkens gesetzt.
  // Existiert unabhängig vom Kategorie-`focusFilter` und kombiniert sich mit
  // ihm (z.B. „nur Aufgaben von Kunde X").
  const [customerFocus, setCustomerFocus] = useState(null); // { id, name } | null
  const [focusFilter, setFocusFilter] = useState(null); // filter key or null
  // Feb 2026 · „Nur offene To-dos"-Filter (lokal, useState). Wird beim Mount
  // aus dem URL-Param `?open=1` initialisiert (Deep-Link aus dem Team-
  // Dashboard „Offene To-dos"-Karte). Umschalten geschieht ausschließlich
  // lokal — kein URL-Sync danach, damit React-Router nicht in eine Endlos-
  // Rerender-Schleife läuft, falls die Route mehrfach navigiert wird.
  const clearOpenFilter = () => {
    setFilterOpenOnly(false);
    if (searchParams.get("open")) {
      const next = new URLSearchParams(searchParams);
      next.delete("open");
      setSearchParams(next, { replace: true });
    }
  };
  const markCancellationsRead = async () => {
    // Optimistic reset — the API call below just persists the timestamp.
    setCancellationsUnread(0);
    try { await api.post("/team/cancellations/mark-read"); } catch { /* silent */ }
  };
  const onChipClick = (key) => async () => {
    if (key === "cancelled") {
      // Independent additive toggle — never affects focusFilter.
      setFilters((prev) => {
        const next = !prev.cancelled;
        if (next && cancellationsUnread > 0) markCancellationsRead();
        return { ...prev, cancelled: next };
      });
      return;
    }
    setFocusFilter((cur) => (cur === key ? null : key));
  };
  const resetAllFilters = () => {
    setFocusFilter(null);
    setFilters(DEFAULT_FILTERS);
  };

  const load = async () => {
    const [e, c, p, u, ex, rq] = await Promise.all([
      api.get("/calendar"),
      api.get("/team/customers"),
      api.get("/properties"),
      api.get("/team/cancellations/unread-count").catch(() => ({ data: { unread: 0 } })),
      api.get("/extras").catch(() => ({ data: [] })),
      // Feb 2026 · Requests-Cache für Kalender-Badge-Klicks
      // (Service-Task-Bündel → RequestDetailModal).
      api.get("/requests").catch(() => ({ data: [] })),
    ]);
    setEvents(e.data);
    setCustomers(c.data);
    setProperties(p.data);
    setCancellationsUnread(u.data?.unread || 0);
    setExtras(ex.data || []);
    setRequestsCache(rq.data || []);
  };
  useEffect(() => {
    load();
    // Live-Poll (alle 15 s) — Änderungen/Stornierungen des Kunden werden hier
    // ohne Seitenneuladen sichtbar (Balken erscheint/verschwindet, STORNIERT-
    // Overlay wechselt auf existierenden Balken).
    const interval = setInterval(() => {
      load().catch(() => {});
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  const grid = useMemo(() => daysInMonthGrid(cursor.getFullYear(), cursor.getMonth()), [cursor]);

  const eventsByDay = useMemo(() => {
    const map = {};
    // Compute enabled kinds.
    //   • Focus mode → nur die eine fokussierte Kategorie.
    //   • Kein Focus → alle nicht-stornierten Kategorien (Fallback zu „Alle").
    //   • `filters.cancelled` beeinflusst hier nichts — Stornierungen sind
    //     ausschließlich Absence-Overlays und werden in `absencesByDay`
    //     gerendert, nicht als Badges.
    const enabledKinds = new Set();
    for (const f of CALENDAR_KIND_FILTERS) {
      if (f.key === "cancelled") continue; // independent toggle, no effect here
      const isEnabled = focusFilter ? (f.key === focusFilter) : true;
      if (isEnabled) f.kinds.forEach((k) => enabledKinds.add(k));
    }
    for (const e of events) {
      // Absences are rendered as multi-day bars (see `absencesByDay`), not as
      // per-day badges. Skip here to avoid duplicating them in the badge row.
      if (e.kind === "absence") continue;
      if (!enabledKinds.has(e.kind)) continue;
      // Customer-focus pin: when a specific customer is pinned, hide all
      // events from other customers so the pinned lane reads as one clean row.
      if (customerFocus && e.customer_id !== customerFocus.id) continue;
      // Feb 2026 · Concierge-Filter · User-Directive „Kalender-Filter liest
      // zugewiesene Einträge nicht korrekt" (1 Sep 2026) · gefixt am
      // 11 Sep 2026 („Charlotte hat keine Einträge im Kalender obwohl
      // Buchungen auf ihren Namen"):
      // Backend scoped für echte Concierges bereits strikt auf
      // `assignee_id/assigned_to == user.id`. Der zusätzliche Client-Filter
      // war fälschlich auch für Real-Concierges aktiv und ließ Einträge
      // fallen, deren Objekt `assigned_concierge_id` NULL hat (z.B. Villa
      // Pfaff). Filter greift jetzt NUR bei Admin-Impersonation UND
      // vergleicht ALLE drei Zuweisungs-Signale (`assignee_id` für manuelle
      // Events, `assigned_to` für die Booking-eigene Direkt-Zuweisung,
      // `assigned_concierge_id` als Property-Haupt-Fallback). Vorher fehlte
      // `assigned_to` — dadurch verschwand Charlottes an Villa Pfaff (Haupt
      // = Ron) zugewiesene Buchung komplett aus ihrer Kalender-Ansicht.
      if (
        isImpersonatingConcierge && activeConciergeId &&
        e.assignee_id !== activeConciergeId &&
        e.assigned_to !== activeConciergeId &&
        e.assigned_concierge_id !== activeConciergeId
      ) continue;
      const s = parseIso(e.start_date);
      const en = parseIso(e.end_date);
      for (let d = new Date(s); d <= en; d.setDate(d.getDate() + 1)) {
        const key = fmtIso(d);
        (map[key] = map[key] || []).push(e);
      }
    }
    return map;
  }, [events, focusFilter, customerFocus, activeConciergeId, isImpersonatingConcierge]);

  // ---- Swimlanes: stable per-customer vertical position ----
  // Each customer keeps a FIXED horizontal row (lane) across the entire
  // calendar view so multi-week absences render at the exact same height in
  // every week — no more jumping when a bar's stack position changes due to
  // insertion order.
  //
  // Lane assignment: sort all absences by their earliest `start_date`; each
  // new customer_id encountered claims the next free lane (0, 1, 2, …).
  // Customers with no absences in this month don't get a lane.
  //
  // Note: cancelled + non-cancelled absences of the SAME customer share a
  // lane (they're the same booking, just at different lifecycle stages).
  const absenceLanes = useMemo(() => {
    const lanes = new Map();
    const absences = events
      .filter((e) => e.kind === "absence")
      // Feb 2026 · Lane-Reservierung · gefixt 11 Sep 2026:
      // Bei Admin-Impersonation die Lane einer Absence NUR reservieren,
      // wenn der impersonierte Concierge tatsächlich zuständig ist —
      // entweder als direkter Buchungs-Verantwortlicher (`assigned_to`)
      // oder als Objekt-Haupt-Concierge (`assigned_concierge_id`).
      // Vorher wurde nur die Property-Haupt-Zuweisung geprüft — Charlottes
      // Buchungen an Villa Pfaff (Haupt = Ron) bekamen dadurch NIE eine
      // Lane und tauchten im Grid nicht auf.
      .filter((e) => !isImpersonatingConcierge || !activeConciergeId
        || e.assigned_to === activeConciergeId
        || e.assigned_concierge_id === activeConciergeId)
      .slice()
      .sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));
    for (const a of absences) {
      const key = a.customer_id || `__event_${a.id}`;
      if (!lanes.has(key)) lanes.set(key, lanes.size);
    }
    return lanes;
  }, [events, activeConciergeId, isImpersonatingConcierge]);

  // ---- Absence bars: one entry per (day, absence) with segment metadata ----
  // Every day the absence covers gets an entry so a subtle gray band renders
  // continuously across the row. Segment metadata tells the cell how to round
  // its corners (start/end of absence OR row boundary) and whether to show the
  // customer name (only on the first cell of each week-row segment).
  //
  // Two independent gates control what shows here:
  //   • `focusFilter` (default null): if focused on a non-absence chip, hide
  //     regular absences (they're rendered separately as long bars).
  //   • `filters.cancelled` (default false): stornierte Abwesenheiten sind
  //     ADDITIV — sie erscheinen nur, wenn der Stornierungen-Chip explizit
  //     angeklickt wurde, unabhängig davon, ob ein Focus-Filter gesetzt ist.
  const absencesByDay = useMemo(() => {
    const showRegular   = !focusFilter || focusFilter === "absence";
    const showCancelled = filters.cancelled;
    if (!showRegular && !showCancelled) return {};
    const map = {};
    for (const e of events) {
      if (e.kind !== "absence") continue;
      const cancelled = e.status === "cancelled";
      if (cancelled && !showCancelled) continue;
      if (!cancelled && !showRegular)  continue;
      // Customer-focus pin: only show this customer's absence bars.
      if (customerFocus && e.customer_id !== customerFocus.id) continue;
      // "Nur offene To-dos"-Filter: fertige und stornierte Abwesenheiten
      // haben keine offenen Team-Aufgaben mehr → aus der Ansicht ausblenden.
      if (filterOpenOnly && (cancelled || e.status === "completed")) continue;
      // Feb 2026 · Absence-Bars · gefixt 11 Sep 2026 („Charlotte hat keine
      // Einträge obwohl Buchungen auf ihren Namen"):
      // Bei Admin-Impersonation die Absence-Bar nur ausblenden, wenn der
      // impersonierte Concierge WEDER direkter Buchungs-Verantwortlicher
      // (`assigned_to`) NOCH Objekt-Haupt-Concierge (`assigned_concierge_id`)
      // ist. Vorher fehlte der `assigned_to`-Zweig — Direkt-Zuweisungen
      // auf einem fremden Objekt (Villa Pfaff/Ron) verschwanden komplett
      // aus der Impersonations-Kalender-Ansicht.
      if (
        isImpersonatingConcierge && activeConciergeId
        && e.assigned_to !== activeConciergeId
        && e.assigned_concierge_id !== activeConciergeId
      ) continue;
      const s = parseIso(e.start_date);
      const en = parseIso(e.end_date);
      // Single source of truth for status colour: derived from the
      // shared 4-colour system (grey/blue/green/red). See lib/bookingStatus.
      //   • Timeline `ankunft.done` (server-side snapshot) → GREEN
      //   • cancelled  → red hatch (own visual, archived)
      //   • else       → RED (Ankunft-Latch: alle Vorbedingungen offen)
      //
      // Feb 2026 · HARDCODED-RULE Ankunft-Latch:
      // Der Kalender-Badge am Ankunftstag ist DIREKT an
      // `e.timeline_stages.ankunft.done` gekoppelt. Solange die Timeline
      // ROT ist (Vorbereitung/Zusatzleistungen/Live-Status offen), bleibt
      // der Absence-Badge ROT — egal wie weit die Anreise in der Zukunft
      // liegt. Fallback: `e.timeline_stages.vorbereitung.done` für Legacy-
      // Events ohne Ankunft-Snapshot; letzter Fallback `e.status === "completed"`.
      const todayIsoStr = fmtIso(today);
      const snapAnkunftDone = e.timeline_stages?.ankunft?.done;
      const snapPrepDone = e.timeline_stages?.vorbereitung?.done;
      const allDone = !cancelled && (
        snapAnkunftDone === true
          || (snapAnkunftDone === undefined && snapPrepDone === true)
          || (snapAnkunftDone === undefined && snapPrepDone === undefined && e.status === "completed")
      );
      const overdue = !cancelled && !allDone && e.end_date && e.end_date < todayIsoStr;
      for (let d = new Date(s); d <= en; d.setDate(d.getDate() + 1)) {
        const key = fmtIso(d);
        const isFirst   = sameDay(d, s);
        const isLast    = sameDay(d, en);
        const isMonday  = ((d.getDay() + 6) % 7) === 0;
        const isSunday  = ((d.getDay() + 6) % 7) === 6;
        // The last day of an absence is the day the customer ARRIVES back
        // home ("Anreisetag"). Rendered as a coloured pill on the bar:
        //   • red   = there are still open checklist tasks for the booking
        //   • green = all tasks are done, house is ready
        // The pill is hidden for cancelled bookings (they get the storniert
        // pill instead).
        const isArrival = isLast && !cancelled;
        const laneKey = e.customer_id || `__event_${e.id}`;
        const lane = absenceLanes.get(laneKey) ?? 0;
        (map[key] = map[key] || []).push({
          id: e.id,
          customer_id: e.customer_id,
          customerName: e.customer_name || (e.title || "").replace(/^Abwesenheit — /, "") || "Kunde",
          subtitle: e.subtitle || "",
          isFirst,
          isLast,
          isArrival,
          lane,
          // Show name on first cell of the absence OR first cell of the week-row
          showLabel: isFirst || isMonday,
          // Round corners only at the true start/end of the absence
          roundLeft:  isFirst,
          roundRight: isLast,
          // "Break" the bar at week boundaries so it doesn't look like it wraps
          openRight: isSunday && !isLast,
          openLeft:  isMonday && !isFirst,
          allDone,
          cancelled,
          overdue,
          // Feb 2026 · User-Directive · Anzahl offener Extras (service_task
          // mit `booking_id === absence.id` und `!done`). Wird in der ersten
          // Zelle der Absence als kleines Gold-Ausrufezeichen-Chip
          // angezeigt, damit der Admin/Concierge sofort erkennt, dass für
          // diese Buchung noch etwas offen ist.
          openExtrasCount: events.filter(
            (x) => x.kind === "service_task"
              && x.booking_id === e.id
              && !x.done
              && x.status !== "cancelled",
          ).length,
          // Draft-preview payload for the hover card (only populated for
          // completed bookings that own an auto-drafted weekly report).
          draft: allDone ? e.weekly_draft || null : null,
        });
      }
    }
    return map;
  }, [events, filters, focusFilter, customerFocus, absenceLanes, filterOpenOnly, activeConciergeId, isImpersonatingConcierge, today]);

  // Total number of swimlanes actually used by the currently VISIBLE
  // absences. Determines how many rows of vertical space each day cell
  // reserves for the absence stack. Only lanes 0..totalAbsenceLanes-1 are
  // rendered — empty lanes at the tail don't take up any height.
  const totalAbsenceLanes = useMemo(() => {
    let max = -1;
    for (const list of Object.values(absencesByDay)) {
      for (const a of list) if (a.lane > max) max = a.lane;
    }
    return max + 1;
  }, [absencesByDay]);

  const filterCounts = useMemo(() => {
    const counts = {};
    for (const f of CALENDAR_KIND_FILTERS) {
      if (f.key === "cancelled") {
        counts[f.key] = events.filter((e) => e.kind === "absence" && e.status === "cancelled").length;
      } else if (f.key === "absence") {
        counts[f.key] = events.filter((e) => e.kind === "absence" && e.status !== "cancelled").length;
      } else {
        counts[f.key] = events.filter((e) => f.kinds.includes(e.kind)).length;
      }
    }
    return counts;
  }, [events]);

  // Group events per day per customer → one clickable badge per {day, customer}
  const badgesByDay = useMemo(() => {
    const todayIso = fmtIso(today);
    const customerName = (id) => {
      const c = customers.find((x) => x.id === id);
      return c ? (c.name || c.email || "Kunde") : "Ohne Kunde";
    };
    const map = {};
    for (const [dayIso, list] of Object.entries(eventsByDay)) {
      const groups = {};
      let weeklyUpdate = false;
      for (const e of list) {
        if (e.kind === "weekly_update") { weeklyUpdate = true; continue; }
        const cid = e.customer_id || "__none__";
        const g = groups[cid] || (groups[cid] = { customer_id: e.customer_id, name: customerName(e.customer_id), total: 0, open: 0, overdue: 0, urgent: 0, cancelled: 0, hasInspection: false, events: [] });
        g.total += 1;
        g.events.push(e);
        const isUrgent = e.priority === "urgent" || e.priority === "high";
        // Feb 2026 · Cancelled service_task/absence: separat zählen, damit
        // ein rein storniertes Bündel den systemweiten Storniert-Look
        // (diagonaler Rot-Streifen) bekommt statt fälschlich Grün.
        if (e.status === "cancelled") g.cancelled += 1;
        if (!e.done) {
          g.open += 1;
          if (dayIso < todayIso) g.overdue += 1;
          if (isUrgent) g.urgent += 1;
        }
        if (e.kind === "inspection") g.hasInspection = true;
      }
      // Sort so red badges (urgent/overdue) come first, then open, then done
      const sorted = Object.values(groups)
        // "Nur offene To-dos"-Filter (Feb 2026): Gruppen ohne offene Aufgaben
        // (alle Termine erledigt) verschwinden komplett — die Ansicht zeigt
        // nur noch Karten mit tatsächlich anstehender Arbeit.
        .filter((g) => !filterOpenOnly || g.open > 0)
        .sort((a, b) => {
          const rank = (g) => g.open === 0 ? 3 : (g.urgent > 0 || g.overdue > 0) ? 0 : 1;
          return rank(a) - rank(b);
        });
      // Weekly-Update-Marker wird ebenfalls nur gezeigt, wenn kein Focus-Filter
      // aktiv ist, der ihn verstecken würde. `filterOpenOnly` blendet ihn aus
      // (Wochenberichte sind Verwaltungs-Nachweise, keine offenen Aufgaben).
      map[dayIso] = {
        badges: sorted,
        weeklyUpdate: filterOpenOnly ? false : weeklyUpdate,
      };
    }
    return map;
  }, [eventsByDay, customers, today, filterOpenOnly]);

  const selectedEvents = useMemo(() => {
    if (!selectedCell) return [];
    const list = eventsByDay[selectedCell.date] || [];
    return list.filter((e) => (e.customer_id || "__none__") === (selectedCell.customer_id || "__none__") && e.kind !== "weekly_update");
  }, [selectedCell, eventsByDay]);

  const selectedCustomerName = useMemo(() => {
    if (!selectedCell) return "";
    if (!selectedCell.customer_id) return "Ohne Kunde";
    const c = customers.find((x) => x.id === selectedCell.customer_id);
    return c ? (c.name || c.email) : "Kunde";
  }, [selectedCell, customers]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.start_date || !form.end_date) return toast.error("Titel und Zeitraum erforderlich.");
    try {
      await api.post("/calendar/events", { ...form, customer_id: form.customer_id || null, property_id: form.property_id || null });
      toast.success("Termin hinzugefügt.");
      setForm({ title: "", kind: "task", start_date: "", end_date: "", customer_id: "", property_id: "", notes: "", color: BRAND_COLORS.navy });
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    }
  };

  const del = async (id) => {
    if (!id.startsWith("event-")) return; // only manual events deletable
    const eventId = id.replace("event-", "");
    if (!(await confirmDialog({
      title: "Termin löschen?",
      description: "Der Termin wird aus dem Kalender entfernt.",
      confirmText: "Löschen",
      variant: "danger",
    }))) return;
    await api.delete(`/calendar/events/${eventId}`);
    await load();
  };

  const monthLabel = `${t.team.months[cursor.getMonth()]} ${cursor.getFullYear()}`;
  // Feb 2026 · User-Directive „Wochennummern-Chip neben jedem Monat"
  // (3 Sep 2026): ISO-8601 Kalenderwoche der aktuellen Woche (Montag als
  // Wochenstart, Woche 1 enthält den 4. Januar). Wird als dezenter
  // Gold-Chip neben `monthLabel` angezeigt, damit der Admin beim Planen
  // sofort sieht, in welcher KW er aktuell steht — unabhängig davon,
  // welchen Monat der Kalender gerade zeigt.
  const isoWeekNo = (d) => {
    const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    const day = t.getUTCDay() || 7;
    t.setUTCDate(t.getUTCDate() + 4 - day);
    const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
    return Math.ceil(((t - yearStart) / 86400000 + 1) / 7);
  };
  const currentIsoWeek = isoWeekNo(today);

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="team-calendar">
      <div className="flex items-start justify-between gap-6 mb-8 flex-wrap">
        <div>
          <div className="label-caps mb-3">Weissblom Team</div>
          <h1 className="font-display text-4xl md:text-5xl text-navy">{t.team.calendar_title}</h1>
          <p className="text-[15px] text-navy-dark/70 mt-3 max-w-2xl">{t.team.calendar_lead}</p>
        </div>
        <button
          data-testid="add-event-btn"
          onClick={() => setShowForm((v) => !v)}
          className="bg-navy text-ivory px-8 py-3 text-[12px] tracking-[0.15em] uppercase hover:bg-navy-dark transition-colors"
          style={{ background: BRAND_COLORS.navy }}
        >
          {showForm ? t.common.close : t.team.add_event}
        </button>
      </div>

      {/* --- Filter-Chips (sticky · persistent via ui_prefs) ---
          Feb 2026 · Bug-Fix: Der refaktorierte `<CalendarFilters/>`-Baustein
          wurde beim Split zwar importiert, aber im JSX nie eingehängt — die
          alte Inline-Duplikat-Ansicht (ohne `sticky` Styles) war die tat-
          sächlich gerenderte. Ersetzt durch echtes Rendern der Komponente,
          damit der Balken direkt unter `--wb-header-h` andockt. */}
      <CalendarFilters
        filters={filters}
        focusFilter={focusFilter}
        customerFocus={customerFocus}
        filterCounts={filterCounts}
        cancellationsUnread={cancellationsUnread}
        onChipClick={onChipClick}
        setFocusFilter={setFocusFilter}
        setCustomerFocus={setCustomerFocus}
      />
      {filterOpenOnly && (
        <button
          type="button"
          onClick={clearOpenFilter}
          className="mb-4 -mt-3 ml-2 inline-flex items-center gap-2 text-[11px] tracking-[0.12em] uppercase px-3 py-1 rounded-full hover:brightness-110 transition-all"
          style={{ background: withAlpha(STATUS_COLORS.red, 0.10), color: STATUS_COLORS.red, border: `1px solid ${withAlpha(STATUS_COLORS.red, 0.28)}` }}
          data-testid="calendar-open-only-hint"
          title='Filter "Nur offene To-dos" aufheben'
        >
          Nur offene To-dos · Fertige ausgeblendet
          <XIcon className="w-3 h-3 opacity-70" strokeWidth={2.4} />
        </button>
      )}

      {showForm && (
        <form onSubmit={submit} className="bg-white border border-navy/10 p-6 mb-8 grid grid-cols-1 md:grid-cols-6 gap-4" data-testid="event-form">
          <input
            data-testid="event-title-input"
            required placeholder="Titel"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="md:col-span-2 bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-navy-dark"
          />
          <select
            data-testid="event-kind-select"
            value={form.kind}
            onChange={(e) => setForm({ ...form, kind: e.target.value })}
            className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3"
          >
            <option value="task">Aufgabe</option>
            <option value="maintenance">Instandhaltung</option>
            <option value="note">Notiz</option>
          </select>
          <input
            data-testid="event-start-input"
            required type="date"
            value={form.start_date}
            onChange={(e) => setForm({ ...form, start_date: e.target.value, end_date: form.end_date || e.target.value })}
            className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3"
          />
          <input
            data-testid="event-end-input"
            required type="date"
            value={form.end_date}
            onChange={(e) => setForm({ ...form, end_date: e.target.value })}
            className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3"
          />
          <select
            data-testid="event-priority-select"
            value={form.priority}
            onChange={(e) => setForm({ ...form, priority: e.target.value })}
            className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-navy-dark"
          >
            <option value="normal">Priorität: Normal</option>
            <option value="high">Priorität: Hoch</option>
            <option value="urgent">Priorität: Dringend / Notfall</option>
            <option value="low">Priorität: Niedrig</option>
          </select>
          <select
            value={form.customer_id}
            onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
            className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3"
          >
            <option value="">Kunde (optional)</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name || c.email}</option>)}
          </select>
          <input
            data-testid="event-notes-input"
            placeholder="Notizen"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="md:col-span-4 bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-navy-dark"
          />
          <button
            data-testid="event-submit-btn"
            type="submit"
            className="md:col-span-2 bg-navy text-ivory py-3 text-[12px] tracking-[0.15em] uppercase hover:bg-navy-dark transition-colors"
            style={{ background: BRAND_COLORS.navy }}
          >
            Termin speichern
          </button>
        </form>
      )}

      {/* Calendar */}
      <div className="bg-white border border-navy/10">
        <div className="flex items-center justify-between p-4 border-b border-navy/10">
          <button
            data-testid="cal-prev-btn"
            onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
            className="text-navy px-3 py-1 border border-navy/15 hover:border-gold transition-colors text-[12px] tracking-wider"
          >
            ← 
          </button>
          <div className="flex items-center gap-2.5">
            <div className="font-display text-2xl text-navy" data-testid="cal-month-label">{monthLabel}</div>
            {/* Feb 2026 · Wochennummern-Chip (User-Directive 3 Sep 2026) —
                dezenter Gold-Rand, keine Fläche, damit er ruhig neben dem
                großen Monatstitel liegt. Zeigt die ISO-KW der GEGENWART,
                nicht des navigierten Monats — der Admin nutzt ihn zur
                Verortung im laufenden Betrieb. */}
            <span
              data-testid="cal-current-week-chip"
              title={`Aktuelle Kalenderwoche · KW ${currentIsoWeek}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium tracking-[0.14em] uppercase border rounded-full whitespace-nowrap"
              style={{
                borderColor: withAlpha(BRAND_COLORS.gold, 0.55),
                color: BRAND_COLORS.gold,
                background: withAlpha(BRAND_COLORS.gold, 0.06),
              }}
            >
              KW {currentIsoWeek}
            </span>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="cal-today-btn"
              onClick={() => setCursor(new Date(today.getFullYear(), today.getMonth(), 1))}
              className="text-navy px-3 py-1 border border-navy/15 hover:border-gold transition-colors text-[12px] tracking-wider uppercase"
            >
              {t.common.today}
            </button>
            <button
              data-testid="cal-next-btn"
              onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
              className="text-navy px-3 py-1 border border-navy/15 hover:border-gold transition-colors text-[12px] tracking-wider"
            >
              →
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 border-b border-navy/10">
          {t.team.weekdays.map((d) => (
            <div key={d} className="label-caps text-center py-3 border-r last:border-r-0 border-navy/10">{d}</div>
          ))}
        </div>

        <div className="grid grid-cols-7" data-testid="cal-grid">
          {grid.map((d, idx) => {
            const key = fmtIso(d);
            const dayCell = badgesByDay[key] || { badges: [], weeklyUpdate: false };
            const absencesForDay = absencesByDay[key] || [];
            const isToday = sameDay(d, today);
            const outOfMonth = d.getMonth() !== cursor.getMonth();
            // Feb 2026 · User-Directive · 3-stelliger DE-Monatsname neben
            // der Tageszahl in jeder Kalenderzelle. Beispiel: „1 Sep",
            // „25 Okt". `toLocaleDateString` liefert je nach Node-Version
            // „Sep." mit Punkt — deshalb feste Map für exakt 3 Buchstaben
            // ohne Punkt.
            const monthAbbr = ["Jan","Feb","Mär","Apr","Mai","Jun","Jul","Aug","Sep","Okt","Nov","Dez"][d.getMonth()];
            return (
              <div key={key} className={`cal-day ${outOfMonth ? "out" : ""} ${isToday ? "today" : ""} ${highlightDay === key ? "ring-2 ring-offset-2 animate-pulse" : ""}`} style={highlightDay === key ? { ["--tw-ring-color"]: BRAND_COLORS.gold } : undefined} data-testid={`cal-day-${key}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-[11px] font-medium ${outOfMonth ? "text-navy/40" : "text-navy-dark/70"}`} data-testid={`cal-day-label-${key}`}>
                    <span>{d.getDate()}</span>
                    <span className={`ml-1 text-[10px] tracking-wide ${outOfMonth ? "text-navy/35" : "text-navy-dark/55"}`}>{monthAbbr}</span>
                  </span>
                  {dayCell.weeklyUpdate && (
                    <span
                      title="Wochenbericht"
                      data-testid={`cal-weekly-dot-${key}`}
                      className="inline-block w-1.5 h-1.5 rounded-full"
                      style={{ background: BRAND_COLORS.gold, boxShadow: `0 0 0 2px ${withAlpha(BRAND_COLORS.gold, 0.2)}` }}
                    />
                  )}
                </div>
                {/* Absence bars FIRST — anchored to the TOP of every day cell.
                    Each customer keeps a STABLE swimlane (see `absenceLanes`)
                    so a multi-week absence renders at the exact same vertical
                    position in every week. Empty lanes render as invisible
                    spacers to preserve alignment across days. */}
                {totalAbsenceLanes > 0 && (
                  <div className="space-y-0.5 mb-1" data-testid={`cal-absences-${key}`}>
                    {Array.from({ length: totalAbsenceLanes }).map((_, laneIdx) => {
                      const a = absencesForDay.find((x) => x.lane === laneIdx);
                      if (!a) {
                        // Invisible spacer — keeps other customers' bars at
                        // their correct height even on days they have no absence.
                        return <div key={`lane-empty-${laneIdx}`} className="h-4" aria-hidden="true" data-testid={`cal-lane-empty-${key}-${laneIdx}`} />;
                      }
                      return (
                      <div
                        key={a.id}
                        role={a.customer_id ? "button" : undefined}
                        tabIndex={a.customer_id ? 0 : undefined}
                        onClick={(e) => {
                          if (!a.customer_id) return;
                          e.stopPropagation();
                          // Cancelled bars open the STORNIERT detail panel — the
                          // team needs to see WHY it was cancelled first.
                          if (a.cancelled) {
                            const full = events.find((ev) => ev.id === a.id);
                            if (full) setCancelledDetail(full);
                            return;
                          }
                          // Active bars open the booking detail modal directly
                          // (in team mode) so the concierge can tick off the
                          // Inklusiv-/Zusatzleistungen without leaving the calendar.
                          const full = events.find((ev) => ev.id === a.id);
                          if (full) {
                            setOpenBooking(buildBookingModalPayload(full));
                          }
                        }}
                        onKeyDown={(e) => {
                          if (!a.customer_id) return;
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            if (a.cancelled) {
                              const full = events.find((ev) => ev.id === a.id);
                              if (full) setCancelledDetail(full);
                              return;
                            }
                            const full = events.find((ev) => ev.id === a.id);
                            if (full) {
                              setOpenBooking(buildBookingModalPayload(full));
                            }
                          }
                        }}
                        className={`relative h-4 flex items-center transition-colors ${a.customer_id ? "cursor-pointer hover:brightness-95" : ""} ${a.cancelled ? "opacity-70" : ""}`}
                        data-testid={`cal-absence-${key}-${a.customer_id || "none"}${a.isArrival ? "-arrival" : ""}${a.cancelled ? "-cancelled" : a.overdue ? "-overdue" : a.allDone ? "-done" : "-open"}`}
                        data-cancelled={a.cancelled ? "true" : undefined}
                        data-overdue={a.overdue ? "true" : undefined}
                        title={`${a.customerName}${a.cancelled ? " · STORNIERT" : a.overdue ? " · ÜBERFÄLLIG" : a.allDone ? " · Erledigt" : ""}${a.subtitle ? " · " + a.subtitle : ""}${a.customer_id ? " · Klick zur Übersicht" : ""}`}
                        onMouseEnter={(ev) => {
                          if (!a.draft) return;
                          const r = ev.currentTarget.getBoundingClientRect();
                          setHoverDraft({
                            id: a.id,
                            customerName: a.customerName,
                            propertyName: a.subtitle || "",
                            draft: a.draft,
                            // Anchor below the bar; center horizontally on cell.
                            top: r.bottom + 6,
                            left: Math.max(12, Math.min(window.innerWidth - 340, r.left - 8)),
                          });
                        }}
                        onMouseLeave={() => setHoverDraft(null)}
                        style={{
                          background: a.cancelled
                            ? `repeating-linear-gradient(135deg, ${withAlpha(STATUS_COLORS.red, 0.10)} 0 6px, ${withAlpha(STATUS_COLORS.red, 0.04)} 6px 12px)`
                            : a.overdue
                              // RED · Fällig/Überfällig — Zeitraum verstrichen, aber nicht abgehakt
                              ? withAlpha(STATUS_COLORS.red, 0.22)
                              : a.allDone
                                // GREEN · Erledigt
                                ? withAlpha(STATUS_COLORS.green, 0.22)
                                // BLUE · In Betreuung / Zugeteilt
                                : withAlpha(STATUS_COLORS.blue, 0.14),
                          borderTopLeftRadius:    a.roundLeft  ? 6 : 0,
                          borderBottomLeftRadius: a.roundLeft  ? 6 : 0,
                          borderTopRightRadius:   a.roundRight ? 6 : 0,
                          borderBottomRightRadius:a.roundRight ? 6 : 0,
                          // Bleed slightly into cell padding on continuation edges so bars appear seamless
                          marginLeft:  a.openLeft  ? "-4px" : 0,
                          marginRight: a.openRight ? "-4px" : 0,
                        }}
                      >
                        {a.showLabel && (
                          <button
                            type="button"
                            onClick={(e) => {
                              // One-click customer focus: pin the whole calendar
                              // to this customer's swimlane. Stop propagation so
                              // the parent bar's booking-detail handler doesn't
                              // fire simultaneously.
                              e.stopPropagation();
                              if (!a.customer_id) return;
                              setCustomerFocus((cur) =>
                                cur && cur.id === a.customer_id
                                  ? null
                                  : { id: a.customer_id, name: a.customerName }
                              );
                            }}
                            data-testid={`cal-customer-focus-${a.customer_id || "none"}`}
                            title={a.customer_id ? `Kalender auf ${a.customerName} fokussieren` : ""}
                            className={`text-[10px] font-bold tracking-wider leading-none px-1.5 whitespace-nowrap overflow-visible pointer-events-auto cursor-pointer hover:underline underline-offset-2 decoration-dotted ${
                              a.cancelled
                                ? "line-through decoration-red-800/70"
                                : ""
                            } ${
                              a.cancelled
                                ? "text-red-800/70"
                                : a.overdue
                                  ? "text-red-800"
                                  : ""
                            }`}
                            style={
                              // Feb 2026 · Nur noch zentrale Farben — die
                              // beiden In-Betreuung / Erledigt-Text-Farben
                              // kommen aus STATUS_COLORS + BRAND_COLORS,
                              // damit die Palette an einem Ort lebt.
                              !a.cancelled && !a.overdue
                                ? { color: a.allDone ? BRAND_COLORS.greenDark : STATUS_COLORS.blue }
                                : undefined
                            }
                          >
                            {/* Feb 2026 · User-Directive · Initialen statt vollen
                                Namen auf Kalender-Balken. Voller Name bleibt im
                                title-Attribut des Balkens erhalten. */}
                            {initialsOf(a.customerName)}
                          </button>
                        )}
                        {/* STORNIERT ribbon — shown on the first cell of the
                            absence so the concierge sees the cancellation at a
                            glance without hunting through the day detail. */}
                        {a.cancelled && a.isFirst && (
                          <span
                            className="absolute right-0.5 top-1/2 -translate-y-1/2 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[8.5px] font-bold tracking-[0.08em] uppercase leading-none z-10 text-ivory"
                            style={{ background: STATUS_COLORS.red, boxShadow: "0 1px 2px rgba(0,0,0,0.15)" }}
                            data-testid={`cal-cancelled-badge-${key}-${a.customer_id || "none"}`}
                          >
                            <XIcon className="w-2.5 h-2.5" strokeWidth={2.6} />
                            <span>Storniert</span>
                          </span>
                        )}
                        {a.isArrival && (
                          <span
                            className="absolute right-0.5 top-1/2 -translate-y-1/2 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold tracking-[0.08em] uppercase leading-none z-10 text-ivory"
                            style={{
                              background: a.allDone ? STATUS_COLORS.green : STATUS_COLORS.red,
                              boxShadow: a.allDone
                                ? "0 1px 2px rgba(0,0,0,0.15)"
                                : `0 0 0 2px ${withAlpha(STATUS_COLORS.red, 0.20)}, 0 1px 2px rgba(0,0,0,0.20)`,
                            }}
                            data-testid={`cal-arrival-badge-${key}-${a.customer_id || "none"}${a.allDone ? "-done" : "-open"}`}
                            title={a.allDone ? "Ankunftstag · Alles bereit" : "Ankunftstag · Aufgaben noch offen"}
                          >
                            {a.allDone
                              ? <Check className="w-2.5 h-2.5" strokeWidth={3} />
                              : <Home className="w-2.5 h-2.5" strokeWidth={2.4} />}
                            <span>Ankunft</span>
                          </span>
                        )}
                        {/* Feb 2026 · User-Directive · Extras-Signal.
                            Wenn der Buchung offene Extraservices/Anfragen
                            zugeordnet sind, erscheint in der ersten Zelle
                            (linke Seite) ein kleines Gold-Chip mit
                            Ausrufezeichen — Admin/Concierge sieht auf
                            einen Blick, wo noch etwas zu tun ist. */}
                        {a.isFirst && !a.cancelled && a.openExtrasCount > 0 && (
                          <span
                            className="absolute left-0.5 top-1/2 -translate-y-1/2 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[8.5px] font-bold tracking-[0.08em] uppercase leading-none z-10"
                            style={{
                              background: BRAND_COLORS.gold,
                              color: BRAND_COLORS.navy,
                              boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
                            }}
                            data-testid={`cal-absence-extras-badge-${key}-${a.customer_id || "none"}`}
                            title={`${a.openExtrasCount} offene Extraleistung${a.openExtrasCount === 1 ? "" : "en"} — Buchungsdetail öffnen`}
                          >
                            <AlertCircle className="w-2.5 h-2.5" strokeWidth={2.6} />
                            <span>{a.openExtrasCount} Extra{a.openExtrasCount === 1 ? "" : "s"}</span>
                          </span>
                        )}
                      </div>
                      );
                    })}
                  </div>
                )}
                {/* Task badges (dated tasks / requests / inspections) — stacked
                    BELOW the absence bar so nothing overlaps. Rendered as a
                    flex-wrap row of pill buttons; auto-wraps to new lines when
                    a day has many entries. */}
                {dayCell.badges.length > 0 && (
                  <div className="flex flex-wrap gap-1 relative z-10" data-testid={`cal-badges-${key}`}>
                    {dayCell.badges.slice(0, 3).map((b) => {
                      const allCancelled = b.total > 0 && b.cancelled === b.total;
                      const allDone = !allCancelled && b.open === 0;
                      const isRed = !allCancelled && (b.urgent > 0 || b.overdue > 0);
                      // Systemweites 4-Farb-Schema:
                      //   GRÜN erledigt · ROT dringend/überfällig · BLAU
                      //   zugeteilt (jemand ist assignee_id auf einem offenen
                      //   Termin) · GRAU angefragt aber niemand zugeteilt.
                      // Feb 2026 · Zusatz-Status ROT gestreift (diagonal)
                      // für rein stornierte Bündel — konsistent mit
                      // Buchungs- und Anfragenliste.
                      const hasAssignee = (b.events || []).some((e) => !e.done && e.assignee_id);
                      const cancelledStripes = `repeating-linear-gradient(135deg, ${STATUS_COLORS.red} 0 4px, ${withAlpha(STATUS_COLORS.red, 0.35)} 4px 8px)`;
                      const bg = allCancelled
                        ? cancelledStripes
                        : allDone
                          ? withAlpha(STATUS_COLORS.green, 0.18)
                          : isRed
                            ? STATUS_COLORS.red
                            : hasAssignee
                              ? STATUS_COLORS.blue
                              : STATUS_COLORS.grey;
                      const fg = allCancelled
                        ? BRAND_COLORS.ivory
                        : (allDone ? STATUS_COLORS.green : BRAND_COLORS.ivory);
                      const state = allCancelled ? "cancelled" : allDone ? "done" : isRed ? "urgent" : hasAssignee ? "assigned" : "unassigned";
                      const isSelected = selectedCell && selectedCell.date === key && (selectedCell.customer_id || "__none__") === (b.customer_id || "__none__");
                      // Feb 2026 · User-Directive „Hover-Tooltip pro Eintrag"
                      // (3 Sep 2026): Der Tooltip zeigt pro Kalender-Eintrag
                      // den zugewiesenen Concierge und stimmt damit mit der
                      // Detailansicht überein. „nicht zugeteilt" gilt jetzt
                      // pro Eintrag — nicht als Sammel-Label für den ganzen
                      // Badge. Erledigte oder stornierte Einträge behalten
                      // ihren Concierge-Namen; sie werden mit ✓/✗ markiert.
                      const summaryParts = [`${b.name}`];
                      if (allCancelled) summaryParts.push(`${b.total} storniert`);
                      else if (allDone) summaryParts.push("Alle erledigt");
                      else {
                        summaryParts.push(`${b.open} offen`);
                        if (b.urgent > 0) summaryParts.push(`${b.urgent} dringend`);
                        if (b.overdue > 0) summaryParts.push(`${b.overdue} überfällig`);
                      }
                      const kindLabel = (e) => {
                        if (e.kind === "service_task") return e.title || "Anfrage";
                        if (e.kind === "inspection") return e.title || "Kontrolle";
                        if (e.kind === "task") return e.title || "Aufgabe";
                        return e.title || (e.kind || "Termin");
                      };
                      const eventLines = (b.events || []).map((e) => {
                        const marker = e.status === "cancelled"
                          ? "✗"
                          : e.done ? "✓" : "•";
                        const concierge = e.assignee?.name?.trim() || "nicht zugeteilt";
                        return `${marker} ${kindLabel(e)} — ${concierge}`;
                      });
                      const tooltipText = [summaryParts.join(" · "), ...(eventLines.length ? ["", ...eventLines] : [])].join("\n");
                      return (
                        <button
                          key={b.customer_id || "__none__"}
                          type="button"
                          onClick={() => {
                            // Feb 2026 · User-Directive · Klick auf ein
                            // Service-Task-Bündel öffnet das RequestDetailModal
                            // für die passende Anfrage. Wenn das Bündel genau
                            // EINE service_task enthält → sofort öffnen.
                            // Sonst weiterhin Cell-selection für den Detail-
                            // Bereich unter dem Kalender.
                            const events = b.events || [];
                            const serviceTasks = events.filter((e) => e.kind === "service_task");
                            const manualEvents = events.filter((e) => e.source === "manual");
                            if (serviceTasks.length === 1) {
                              const only = serviceTasks[0];
                              const rawId = only.id.replace(/^servicereq-/, "");
                              const cached = requestsCache.find((r) => r.id === rawId);
                              if (cached) {
                                setOpenRequest(cached);
                                return;
                              }
                              // Fallback: on-demand fetch, falls Cache leer ist.
                              api.get("/requests").then(({ data }) => {
                                const fresh = (data || []).find((r) => r.id === rawId);
                                if (fresh) setOpenRequest(fresh);
                                else setSelectedCell({ date: key, customer_id: b.customer_id });
                              }).catch(() => setSelectedCell({ date: key, customer_id: b.customer_id }));
                              return;
                            }
                            if (manualEvents.length === 1) {
                              const only = manualEvents[0];
                              const rawId = only.id.replace(/^event-/, "");
                              setChecklistEventId(rawId);
                              setSelectedCell({ date: key, customer_id: b.customer_id });
                            } else {
                              setSelectedCell({ date: key, customer_id: b.customer_id });
                            }
                          }}
                          data-testid={`cal-badge-${key}-${b.customer_id || "none"}-${state}`}
                          title={tooltipText}
                          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium leading-none shadow-sm transition-transform hover:scale-105 ${isSelected ? "ring-2 ring-offset-1" : ""} ${isRed && !allDone && !allCancelled ? "animate-pulse" : ""}`}
                          style={{ background: bg, color: fg, boxShadow: `0 1px 3px ${withAlpha(BRAND_COLORS.navy, 0.18)}`, ...(isSelected ? { boxShadow: `0 0 0 2px #C9A961, 0 1px 3px ${withAlpha(BRAND_COLORS.navy, 0.18)}` } : {}) }}
                        >
                          {allCancelled
                            ? <XIcon className="w-3 h-3" strokeWidth={3} />
                            : allDone
                              ? <Check className="w-3 h-3" strokeWidth={3} />
                              : <span className="tabular-nums">{b.open}</span>}
                          {/* Feb 2026 · User-Directive · Initialen statt vollem
                              Namen auf Task-Badges. Voller Name bleibt im
                              title-Attribut (siehe `tooltipParts`) erhalten.
                              Der Punkt-Separator trennt Zähler und Initialen
                              visuell, damit „31 · EL" nicht wie „31EL" liest. */}
                          {!allCancelled && !allDone && <span className="opacity-60">·</span>}
                          <span className="font-bold tracking-wider">{initialsOf(b.name)}</span>
                        </button>
                      );
                    })}
                    {dayCell.badges.length > 3 && (
                      <span className="text-[10px] text-navy-dark/50 self-center">+{dayCell.badges.length - 3}</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* --- Booking Detail Modal (team mode) — opened when the team clicks
          an ACTIVE (non-cancelled) absence bar. Shows the compact Aufgaben-
          Checkliste (only checked services + Service hinzufügen-Button).
          When the last item flips to done, the backend auto-drafts a weekly
          status report for this customer. */}
      {openBooking && (
        <BookingDetailModal
          booking={openBooking}
          extras={extras}
          propertyName={openBooking._property_name || ""}
          mode="team"
          onClose={() => setOpenBooking(null)}
          onChanged={() => { load().catch(() => {}); }}
        />
      )}

      {/* --- Hover-Vorschau des Auto-Drafts über einem ERLEDIGT-Balken ---
          Fixed-positioned so it never gets clipped by day-cell overflow.
          Pointer-events off means the popover never steals focus back — the
          team simply moves the mouse away and it disappears. */}
      {hoverDraft && hoverDraft.draft && (
        <div
          className="fixed z-[60] w-[320px] pointer-events-none animate-in fade-in slide-in-from-top-1 duration-150"
          style={{ top: hoverDraft.top, left: hoverDraft.left }}
          data-testid="cal-hover-draft-preview"
          role="tooltip"
        >
          <div
            className="border shadow-2xl bg-white p-4"
            style={{ borderColor: withAlpha(STATUS_COLORS.green, 0.4), borderTopWidth: "3px", borderTopColor: STATUS_COLORS.green }}
          >
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="min-w-0">
                <div
                  className="text-[9.5px] tracking-[0.18em] uppercase font-bold mb-1"
                  style={{ color: STATUS_COLORS.green }}
                >
                  Wochenbericht-Entwurf · bereit zur Freigabe
                </div>
                <div className="font-display text-[15px] text-navy leading-tight truncate">
                  {hoverDraft.customerName}
                </div>
                {hoverDraft.propertyName && (
                  <div className="text-[11px] text-navy-dark/55 truncate mt-0.5">{hoverDraft.propertyName}</div>
                )}
              </div>
              <span
                className="inline-flex items-center justify-center min-w-[24px] px-2 py-0.5 rounded-full text-[11px] font-semibold text-ivory shrink-0"
                style={{ background: STATUS_COLORS.green }}
                data-testid="cal-hover-draft-count"
                title={`${hoverDraft.draft.activities_count} Aufgaben erledigt`}
              >
                {hoverDraft.draft.activities_count}
              </span>
            </div>
            {hoverDraft.draft.summary && (
              <div
                className="text-[11.5px] text-navy-dark/80 leading-relaxed whitespace-pre-line line-clamp-6 border-l-2 pl-2.5"
                style={{ borderColor: withAlpha(BRAND_COLORS.gold, 0.5), maxHeight: "9em", overflow: "hidden" }}
                data-testid="cal-hover-draft-summary"
              >
                {hoverDraft.draft.summary}
              </div>
            )}
            <div
              className="mt-2.5 pt-2 border-t text-[10px] tracking-[0.12em] uppercase text-navy-dark/50 flex items-center justify-between"
              style={{ borderColor: withAlpha(BRAND_COLORS.navy, 0.06) }}
            >
              <span>Klick zum Öffnen</span>
              {hoverDraft.draft.auto_generated && <span>Auto-generiert</span>}
            </div>
          </div>
        </div>
      )}

      {/* --- STORNIERT Detail Panel — opened when the team clicks a cancelled bar --- */}
      {cancelledDetail && (() => {
        const cd = cancelledDetail;
        const cancelledAtLabel = cd.cancelled_at
          ? (() => {
              try {
                return deWeekdayDateFullTime(cd.cancelled_at);
              } catch { return cd.cancelled_at; }
            })()
          : "—";
        const fromLabel = deDateFull(cd.start_date);
        const toLabel   = deDateFull(cd.end_date);
        const nights = Math.max(0, Math.round((new Date(cd.end_date) - new Date(cd.start_date)) / 86400000));
        return (
          <section
            className="mt-6 border p-5 md:p-6"
            style={{
              background: withAlpha(STATUS_COLORS.red, 0.03),
              borderColor: withAlpha(STATUS_COLORS.red, 0.35),
            }}
            data-testid="cal-cancelled-detail-panel"
          >
            <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
              <div className="flex items-start gap-3 min-w-0">
                <div className="shrink-0 mt-0.5 w-9 h-9 rounded-full flex items-center justify-center" style={{ background: STATUS_COLORS.red }}>
                  <XCircle className="w-5 h-5 text-ivory" strokeWidth={1.8} />
                </div>
                <div className="min-w-0">
                  <div
                    className="text-[10.5px] tracking-[0.18em] uppercase font-bold mb-1"
                    style={{ color: STATUS_COLORS.red }}
                    data-testid="cancelled-detail-label"
                  >
                    Stornierte Abwesenheit
                  </div>
                  <h3 className="font-display text-2xl text-navy leading-tight" data-testid="cancelled-detail-customer">
                    {cd.customer_name || "Kunde"}
                  </h3>
                  {cd.customer_email && (
                    <div className="text-[12px] text-navy-dark/60 mt-0.5">{cd.customer_email}</div>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setCancelledDetail(null)}
                data-testid="cancelled-detail-close"
                className="text-[11px] tracking-widest uppercase text-navy-dark/60 hover:text-navy"
              >
                Schließen ×
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {/* Original desired range */}
              <div className="border-l-4 border-navy/20 pl-4 py-1">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-3.5 h-3.5 text-navy-dark/60" strokeWidth={1.7} />
                  <div className="label-caps">Ursprünglicher Wunschzeitraum</div>
                </div>
                <div
                  className="text-[15px] text-navy font-display leading-snug line-through decoration-red-800/50"
                  data-testid="cancelled-detail-range"
                >
                  {fromLabel} <ArrowRight className="inline w-3.5 h-3.5 mx-1 -mt-1" strokeWidth={1.6} /> {toLabel}
                </div>
                <div className="text-[11.5px] text-navy-dark/55 mt-1">
                  {nights} {nights === 1 ? "Nacht" : "Nächte"} · geplante Abwesenheit
                </div>
              </div>

              {/* Cancellation info */}
              <div className="border-l-4 pl-4 py-1" style={{ borderColor: STATUS_COLORS.red }}>
                <div className="flex items-center gap-2 mb-2">
                  <UserIcon className="w-3.5 h-3.5" strokeWidth={1.7} style={{ color: STATUS_COLORS.red }} />
                  <div className="label-caps" style={{ color: STATUS_COLORS.red }}>Abgesagt vom Kunden</div>
                </div>
                <div
                  className="text-[15px] text-navy font-display leading-snug"
                  data-testid="cancelled-detail-timestamp"
                >
                  {cancelledAtLabel}
                </div>
                <div className="text-[11.5px] text-navy-dark/55 mt-1">
                  Direkt aus der Kunden-App storniert
                </div>
              </div>
            </div>

            {(cd.property_name || cd.notes) && (
              <div className="mt-5 pt-4 border-t border-navy/10 space-y-2.5">
                {cd.property_name && (
                  <div className="flex items-baseline gap-2 text-[13px]">
                    <MapPin className="w-3.5 h-3.5 text-navy-dark/50 shrink-0 mt-0.5" strokeWidth={1.7} />
                    <span className="label-caps text-navy-dark/60 mr-1">Objekt:</span>
                    <span className="text-navy" data-testid="cancelled-detail-property">{cd.property_name}</span>
                  </div>
                )}
                {cd.notes && (
                  <div className="border-l-2 border-gold/60 pl-3 text-[12.5px] text-navy-dark/75 italic leading-relaxed" data-testid="cancelled-detail-notes">
                    „{cd.notes}"
                  </div>
                )}
              </div>
            )}

            <div className="mt-5 pt-4 border-t border-navy/10 flex items-center justify-end gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setCancelledDetail(null)}
                data-testid="cancelled-detail-dismiss"
                className="text-[11px] tracking-widest uppercase text-navy-dark/60 hover:text-navy px-4 py-2"
              >
                Zurück zum Kalender
              </button>
              {cd.customer_id && (
                <button
                  type="button"
                  onClick={() => navigate(`/team/customers/${cd.customer_id}`)}
                  data-testid="cancelled-detail-open-customer"
                  className="inline-flex items-center gap-2 border border-navy text-navy hover:bg-navy hover:text-ivory px-4 py-2 text-[11px] tracking-[0.18em] uppercase transition-colors"
                >
                  Zum Kunden
                  <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.6} />
                </button>
              )}
            </div>
          </section>
        );
      })()}

      {/* --- Details für ausgewählten Tag × Kunde --- */}
      {selectedCell && (
        <section className="mt-6 bg-white border border-gold/40 p-5 md:p-6" data-testid="cal-detail-panel">
          <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
            <div>
              <div className="label-caps mb-1" style={{ color: BRAND_COLORS.gold }}>
                {deDateLong(selectedCell.date)}
              </div>
              <h3 className="font-display text-2xl text-navy">{selectedCustomerName}</h3>
              <p className="text-[12.5px] text-navy-dark/60 mt-1">
                {selectedEvents.length} Termin{selectedEvents.length === 1 ? "" : "e"}
                {" · "}{selectedEvents.filter((e) => !e.done).length} offen
                {" · "}{selectedEvents.filter((e) => e.done).length} erledigt
              </p>
            </div>
            <button
              type="button"
              onClick={() => setSelectedCell(null)}
              data-testid="cal-detail-close"
              className="text-[11px] tracking-widest uppercase text-navy-dark/60 hover:text-navy"
            >
              Schließen ×
            </button>
          </div>
          <ul className="space-y-2" data-testid="cal-detail-list">
            {selectedEvents.map((e) => {
              const overdue = !e.done && selectedCell.date < fmtIso(today);
              return (
                <li key={e.id} className={`flex items-center gap-3 border p-3 transition-opacity ${e.done ? "opacity-60" : ""}`}
                  style={e.done ? { background: withAlpha(STATUS_COLORS.green, 0.06), borderColor: withAlpha(STATUS_COLORS.green, 0.25) } : overdue ? { background: withAlpha(STATUS_COLORS.red, 0.04), borderColor: withAlpha(STATUS_COLORS.red, 0.3) } : { borderColor: withAlpha(BRAND_COLORS.navy, 0.12) }}
                  data-testid={`cal-detail-event-${e.id}`}
                >
                  <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                    style={
                      // Hintergrund STRIKT nach Status:
                      //   grün = erledigt · rot = überfällig · blau = zugeteilt (in Betreuung)
                      //   grau = angefragt / kein Assignee
                      // Die KATEGORIE des Termins wird ausschließlich durch das
                      // Icon-Kürzel im Kreis + das Label rechts kommuniziert.
                      e.done
                        ? { background: STATUS_COLORS.green }
                        : overdue
                          ? { background: STATUS_COLORS.red }
                          : e.assignee_id
                            ? { background: STATUS_COLORS.blue }
                            : { background: STATUS_COLORS.grey }
                    }>
                    {e.done ? <Check className="w-3.5 h-3.5" style={{ color: BRAND_COLORS.ivory }} strokeWidth={3} /> : <span className="text-[10px] text-ivory tracking-wider uppercase">{e.kind[0]}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-[14px] font-medium ${e.done ? "text-navy-dark/60 line-through decoration-navy-dark/30" : "text-navy"}`}>{e.title}</div>
                    {e.subtitle && <div className="text-[12px] text-navy-dark/60 truncate">{e.subtitle}</div>}
                    {e.assignee && (
                      <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-navy-dark/70" data-testid={`cal-detail-assignee-${e.id}`}>
                        <Avatar name={e.assignee.name} fileId={e.assignee.avatar_file_id} size={20} />
                        <span>{e.assignee.name}</span>
                      </div>
                    )}
                  </div>
                  <span className="label-caps shrink-0" style={{ color: overdue ? STATUS_COLORS.red : BRAND_COLORS.gold }}>
                    {e.done ? "Erledigt" : overdue ? "Überfällig" : "Aufgabe"}
                  </span>
                  {e.source === "manual" && (
                    <button
                      type="button"
                      onClick={() => setChecklistEventId(e.id.replace(/^event-/, ""))}
                      data-testid={`cal-detail-open-checklist-${e.id}`}
                      className="inline-flex items-center gap-1.5 border border-navy text-navy px-3 py-1.5 text-[10.5px] tracking-[0.12em] uppercase hover:bg-navy hover:text-ivory transition-colors"
                    >
                      Checkliste öffnen →
                    </button>
                  )}
                </li>
              );
            })}
            {selectedEvents.length === 0 && (
              <li className="text-[13px] text-navy-dark/50 italic">Keine Termine für diesen Kunden an diesem Tag.</li>
            )}
          </ul>
        </section>
      )}

      <div className="mt-6 flex flex-wrap gap-4 text-[11px] tracking-widest uppercase text-navy-dark/60">
        <span className="inline-flex items-center gap-2" data-testid="legend-absence-bar">
          <span
            className="inline-block h-3 w-8 rounded-md"
            style={{ background: withAlpha(STATUS_COLORS.blue, 0.14) }}
          />
          Abwesenheits-Zeitraum (in Betreuung)
        </span>
        <span className="inline-flex items-center gap-1.5" data-testid="legend-status-unassigned">
          <span className="inline-flex items-center justify-center rounded-full px-2 py-0.5 text-[10px] font-medium" style={{ background: STATUS_COLORS.grey, color: BRAND_COLORS.navy }}>2 Kunde</span>
          Angefragt · nicht zugeteilt
        </span>
        <span className="inline-flex items-center gap-1.5" data-testid="legend-status-assigned">
          <span className="inline-flex items-center justify-center rounded-full px-2 py-0.5 text-[10px] font-medium" style={{ background: STATUS_COLORS.blue, color: BRAND_COLORS.ivory }}>3 Kunde</span>
          In Betreuung · zugeteilt
        </span>
        <span className="inline-flex items-center gap-1.5" data-testid="legend-status-done">
          <span className="inline-flex items-center justify-center rounded-full px-2 py-0.5 text-[10px] font-medium gap-1" style={{ background: withAlpha(STATUS_COLORS.green, 0.18), color: STATUS_COLORS.green }}>
            <Check className="w-2.5 h-2.5" strokeWidth={3} />Kunde
          </span>
          Erledigt
        </span>
        <span className="inline-flex items-center gap-1.5" data-testid="legend-status-overdue">
          <span className="inline-flex items-center justify-center rounded-full px-2 py-0.5 text-[10px] font-medium" style={{ background: STATUS_COLORS.red, color: BRAND_COLORS.ivory }}>2 Kunde</span>
          Fällig / Überfällig
        </span>
        <span className="inline-flex items-center gap-2" data-testid="legend-absence-cancelled">
          <span
            className="inline-block h-3 w-8 rounded-md relative"
            style={{
              background: `repeating-linear-gradient(135deg, ${withAlpha(STATUS_COLORS.red, 0.10)} 0 6px, ${withAlpha(STATUS_COLORS.red, 0.04)} 6px 12px)`,
            }}
          />
          Stornierte Abwesenheit
        </span>
        <span className="inline-flex items-center gap-2" data-testid="legend-arrival-open">
          <span
            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] text-ivory font-bold uppercase tracking-[0.08em]"
            style={{ background: STATUS_COLORS.red }}
          >
            <Home className="w-2.5 h-2.5" strokeWidth={2.4} />Ankunft
          </span>
          Ankunftstag · Aufgaben noch offen
        </span>
        <span className="inline-flex items-center gap-2" data-testid="legend-arrival-done">
          <span
            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] text-ivory font-bold uppercase tracking-[0.08em]"
            style={{ background: STATUS_COLORS.green }}
          >
            <Check className="w-2.5 h-2.5" strokeWidth={3} />Ankunft
          </span>
          Alles bereit für die Rückreise
        </span>
        <span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: BRAND_COLORS.gold, boxShadow: `0 0 0 2px ${withAlpha(BRAND_COLORS.gold, 0.2)}` }} /> Wochenbericht gesendet</span>
      </div>

      <EventChecklistDialog
        eventId={checklistEventId}
        open={!!checklistEventId}
        onClose={() => setChecklistEventId(null)}
        onChanged={load}
      />
      {/* Feb 2026 · Kalender ↔ Anfragen-Kreisweg · Klick auf ein
          Service-Task-Bündel öffnet die vollständige Detailansicht der
          zugehörigen Anfrage. Beim Verknüpfen/Entkoppeln eines Angebots
          reloadet der Kalender die events, damit der Farbcode aktuell bleibt. */}
      {openRequest && (
        <RequestDetailModal
          request={openRequest}
          onClose={() => setOpenRequest(null)}
          onLinkedChange={() => { setOpenRequest(null); load(); }}
        />
      )}
    </div>
  );
}
